import { Box, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { APP_LOCALE } from '../../utils/locale.js';

/**
 * The header every settings section shares (DEV-133): its title, what it is
 * for, and when it was last changed and by whom, when that is known. Its
 * actions (the History button, a section's own "New ..." button) sit beside
 * the title (DEV-137).
 */

interface Props {
  readonly title: string;
  readonly children?: ReactNode;
  /** `of` names what the date belongs to when it is wider than the section. */
  readonly lastChanged?: { readonly at: string | null; readonly by: string | null; readonly of?: string } | undefined;
  readonly actions?: ReactNode;
}

export function SettingsSectionHeader({ title, children, lastChanged, actions }: Props): JSX.Element {
  return (
    <Box sx={{ mb: 3, maxWidth: actions === undefined ? 820 : undefined }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h5" gutterBottom sx={{ flex: 1, minWidth: 0 }}>{title}</Typography>
        {actions}
      </Stack>
      {children !== undefined ? <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 820 }}>{children}</Typography> : null}
      {lastChanged !== undefined ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {lastChanged.at === null
            ? 'Never changed.'
            : `${lastChanged.of ?? 'Last'} changed ${new Date(lastChanged.at).toLocaleString(APP_LOCALE)} by ${lastChanged.by ?? 'unknown'}.`}
        </Typography>
      ) : null}
    </Box>
  );
}
